import { useState, useEffect } from "react";
import Header from "../components/Header";
import { 
  Play, 
  Eye, 
  Heart, 
  Clock, 
  TrendingUp,
  Calendar,
  Share2,
  Filter,
  Grid3x3,
  List,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return "/api/images/hero_culture_baoule.png";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

// Extract YouTube video ID from URL
const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filter, setFilter] = useState('all'); // 'all', 'recent', 'popular'
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);

  useEffect(() => {
    fetch(`${API}/cms/homepage`)
      .then(res => res.json())
      .then(data => {
        setVideos(data.videos || []);
        setFilteredVideos(data.videos || []);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    let result = [...videos];
    
    switch (filter) {
      case 'recent':
        result.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        break;
      case 'popular':
        result.sort((a, b) => (parseInt(b.views) || 0) - (parseInt(a.views) || 0));
        break;
      default:
        break;
    }
    
    setFilteredVideos(result);
  }, [filter, videos]);

  const openVideoModal = (video) => {
    setCurrentVideo(video);
    setVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
    setCurrentVideo(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
              📹 Vidéos de la Semaine
            </h1>
            <p className="text-muted-foreground">
              Découvrez nos contenus vidéo sur la culture baoulé
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-primary">{videos.length}</p>
              <p className="text-sm text-muted-foreground">Vidéos</p>
            </div>
            <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-secondary">
                {videos.reduce((acc, v) => acc + (parseInt(v.views) || 0), 0)}
              </p>
              <p className="text-sm text-muted-foreground">Vues totales</p>
            </div>
            <div className="bg-gradient-to-br from-accent/10 to-accent/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-accent">
                {videos.reduce((acc, v) => acc + (parseInt(v.likes) || 0), 0)}
              </p>
              <p className="text-sm text-muted-foreground">J'aime</p>
            </div>
            <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-orange-500">12</p>
              <p className="text-sm text-muted-foreground">Nouvelles</p>
            </div>
          </div>

          {/* Filters & View Mode */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            {/* Filters */}
            <div className="flex items-center gap-2 bg-muted p-1 rounded-lg">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                  filter === 'all' 
                    ? 'bg-primary text-white' 
                    : 'hover:bg-background text-foreground'
                }`}
              >
                Toutes
              </button>
              <button
                onClick={() => setFilter('recent')}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors flex items-center gap-2 ${
                  filter === 'recent' 
                    ? 'bg-primary text-white' 
                    : 'hover:bg-background text-foreground'
                }`}
              >
                <Calendar className="w-4 h-4" />
                Récentes
              </button>
              <button
                onClick={() => setFilter('popular')}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors flex items-center gap-2 ${
                  filter === 'popular' 
                    ? 'bg-primary text-white' 
                    : 'hover:bg-background text-foreground'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                Populaires
              </button>
            </div>

            {/* View Mode */}
            <div className="flex items-center gap-2 bg-muted p-1 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid' ? 'bg-primary text-white' : 'hover:bg-background'
                }`}
                title="Grille"
              >
                <Grid3x3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' ? 'bg-primary text-white' : 'hover:bg-background'
                }`}
                title="Liste"
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Videos Grid/List */}
          {filteredVideos.length > 0 ? (
            <div className={
              viewMode === 'grid' 
                ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' 
                : 'space-y-6'
            }>
              {filteredVideos.map((video, i) => (
                <div 
                  key={video.id || i} 
                  className={`video-card animate-slide-up cursor-pointer group ${
                    viewMode === 'list' ? 'flex gap-6 items-start' : ''
                  }`}
                  style={{animationDelay: `${i * 0.05}s`}}
                  onClick={() => openVideoModal(video)}
                >
                  {/* Thumbnail */}
                  <div className={`relative overflow-hidden rounded-xl ${
                    viewMode === 'list' ? 'w-80 flex-shrink-0' : ''
                  }`}>
                    <img 
                      src={getImageUrl(video.image_url)} 
                      alt={video.title} 
                      className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" 
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors" />
                    
                    {/* Play Button */}
                    <div className="play-button group-hover:scale-110 group-hover:bg-primary transition-all">
                      <Play className="w-8 h-8 fill-current" />
                    </div>
                    
                    {/* Duration */}
                    <div className="absolute bottom-3 right-3 bg-black/80 px-2 py-1 rounded text-xs font-semibold text-white">
                      {video.duration}
                    </div>

                    {/* Category Badge */}
                    {video.category && (
                      <div className="absolute top-3 left-3 bg-primary px-2 py-1 rounded text-xs font-semibold text-white">
                        {video.category}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className={`p-5 ${viewMode === 'grid' ? 'bg-card rounded-b-xl' : 'flex-1'}`}>
                    <h3 className="font-heading text-lg font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {video.title}
                    </h3>
                    
                    {video.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {video.description}
                      </p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" /> 
                        {video.views || '0'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-4 h-4" /> 
                        {video.likes || '0'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" /> 
                        {video.duration}
                      </span>
                    </div>

                    {video.author && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-sm font-bold">
                            {video.author.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{video.author}</p>
                            {video.date && (
                              <p className="text-xs text-muted-foreground">{video.date}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                <Play className="w-12 h-12 text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2">
                Aucune vidéo pour le moment
              </h3>
              <p className="text-muted-foreground mb-4">
                Les vidéos seront bientôt disponibles !
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Video Modal */}
      {videoModalOpen && currentVideo && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-fade-in"
          onClick={closeVideoModal}
        >
          <div 
            className="relative w-full max-w-5xl bg-background rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeVideoModal}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Video Player */}
            <div className="aspect-video bg-black">
              {getYouTubeId(currentVideo.video_url) ? (
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${getYouTubeId(currentVideo.video_url)}?autoplay=1`}
                  title={currentVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <div className="text-center">
                    <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Vidéo non disponible</p>
                  </div>
                </div>
              )}
            </div>

            {/* Video Info */}
            <div className="p-6">
              <h2 className="font-heading text-2xl font-bold mb-2">
                {currentVideo.title}
              </h2>
              
              {currentVideo.description && (
                <p className="text-muted-foreground mb-4">
                  {currentVideo.description}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" /> 
                  {currentVideo.views || '0'} vues
                </span>
                <span className="flex items-center gap-1">
                  <Heart className="w-4 h-4" /> 
                  {currentVideo.likes || '0'} j'aime
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" /> 
                  {currentVideo.duration}
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors">
                  <Heart className="w-5 h-5" />
                  J'aime
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors">
                  <Share2 className="w-5 h-5" />
                  Partager
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
